import { create } from 'zustand';
import { Task } from '../types';
import { tasksApi } from '../services/api';

interface TaskState {
  tasks: Task[];
  currentTask: Task | null;
  loading: boolean;
  error: string | null;
  fetchTasks: (projectId: string) => Promise<void>;
  createTask: (data: Partial<Task>) => Promise<Task>;
  updateTask: (id: string, data: Partial<Task>) => Promise<void>;
  deleteTask: (id: string) => Promise<void>;
  moveTask: (id: string, status: Task['status'], position: number) => Promise<void>;
}

export const useTaskStore = create<TaskState>((set, get) => ({
  tasks: [],
  currentTask: null,
  loading: false,
  error: null,

  fetchTasks: async (projectId) => {
    set({ loading: true });
    try {
      const response = await tasksApi.getByProject(projectId);
      set({ tasks: response.data, loading: false });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch tasks';
      set({ error: message, loading: false });
    }
  },

  createTask: async (data) => {
    const response = await tasksApi.create(data);
    const newTask = response.data;
    set({ tasks: [...get().tasks, newTask] });
    return newTask;
  },

  updateTask: async (id, data) => {
    const response = await tasksApi.update(id, data);
    const updated = response.data;
    set({ tasks: get().tasks.map(t => t.id === id ? updated : t) });
  },

  deleteTask: async (id) => {
    await tasksApi.delete(id);
    set({ tasks: get().tasks.filter(t => t.id !== id) });
  },

  moveTask: async (id, status, position) => {
    const previousTasks = get().tasks;
    const movingTask = previousTasks.find(t => t.id === id);
    if (!movingTask) return;

    const sourceStatus = movingTask.status;
    const projectTasks = previousTasks.filter(t => t.project_id === movingTask.project_id);
    const sourceTasks = projectTasks
      .filter(t => t.status === sourceStatus && t.id !== id)
      .sort((a, b) => a.position - b.position);
    const targetTasks = (sourceStatus === status
      ? sourceTasks
      : projectTasks.filter(t => t.status === status && t.id !== id)
    ).sort((a, b) => a.position - b.position);
    const safePosition = Math.max(0, Math.min(position, targetTasks.length));

    targetTasks.splice(safePosition, 0, { ...movingTask, status, position: safePosition });

    const reordered = new Map<string, Task>();
    if (sourceStatus !== status) {
      sourceTasks.forEach((task, index) => reordered.set(task.id, { ...task, position: index }));
    }
    targetTasks.forEach((task, index) => reordered.set(task.id, { ...task, status, position: index }));

    set({
      tasks: previousTasks.map(t =>
        reordered.get(t.id) || t
      )
    });
    try {
      await tasksApi.updatePosition(id, status, position);
    } catch {
      const task = get().tasks.find(t => t.id === id);
      if (task?.project_id) {
        get().fetchTasks(task.project_id);
      }
    }
  },
}));
